import {Selection} from 'components/FluentUICompat';
import {Callout, Check, Theme} from '@fluentui/react';
import {IRow} from './GridViewRows';
import {GridViewColumn, GridViewColumnType} from './GridViewColumns';
import {RenderEditCellProps} from 'react-data-grid';
import {
	renderArrayField,
	renderDate,
	renderPhase,
	renderRichtext,
} from '../ColumnRenderers';
import {ActionButton} from 'components/ActionButton';
import {
	ControlledDatePicker,
	ControlledTagPicker,
	ControlledTextField,
	PhasePicker,
} from 'components/hookForms';
import {Control} from 'react-hook-form';
import {DataStoreGridChangedCells} from './GridView';
import {ControlledRichtextEditor} from 'components/hookForms/ControlledRichtextEditor';

export function renderCheckbox(
	selection: React.MutableRefObject<Selection<IRow>>,
) {
	const renderCell = ({row}: {row: IRow}) => {
		return (
			<div
				key={'chk' + row.rowId}
				style={{padding: '5px 8px'}}
				data-selection-index={row.rowId}
				onClick={e => {
					if (e.type === 'click') {
						const arrItemsSelected = selection.current.getSelection();
						if (e.shiftKey && arrItemsSelected.length > 0) {
							const intCurrent = row.rowId;
							const intTop = arrItemsSelected[0].rowId;
							const intBottom =
								arrItemsSelected[arrItemsSelected.length - 1].rowId;
							const intStart = intTop < intCurrent ? intTop : intCurrent;
							const intNumber =
								intTop < intCurrent
									? intCurrent - intTop + 1
									: intBottom - intCurrent + 1;
							selection.current.setRangeSelected(
								intStart,
								intNumber,
								true,
								false,
							);
						} else {
							selection.current.setIndexSelected(
								row.rowId,
								!selection.current.isIndexSelected(row.rowId),
								false,
							);
						}
					}
				}}
			>
				<Check checked={selection.current.isIndexSelected(row.rowId)} />
			</div>
		);
	};
	return renderCell;
}

export function renderCellSTD(col: GridViewColumn) {
	const props = {
		key: col.key,
		name: col.name,
		fieldName: col.key,
		minWidth: 100,
	};

	let renderer;

	switch (col.columnType) {
		case GridViewColumnType.Array:
			renderer = renderArrayField();
			break;

		case GridViewColumnType.Date:
			renderer = renderDate();
			break;

		case GridViewColumnType.Phase:
			renderer = renderPhase();
			break;

		case GridViewColumnType.HTML:
			renderer = renderRichtext();
			break;

		default:
			renderer = (item: any, i: number, col: any) => <>{item[props.key]}</>;
	}

	return ({row}: {row: IRow}) => renderer!(row.item, 0, props);
}

export function renderCellHighlightSTD(
	col: GridViewColumn,
	dsChangedCells: DataStoreGridChangedCells,
	theme: Theme,
) {
	const {key, renderCell} = col;

	const renderCellDefault = ({row}: {row: IRow}) => <></>;
	const renderCellF = renderCell ?? renderCellDefault;

	return ({row}: {row: IRow}) => {
		const item = dsChangedCells[row.item.id];
		const didChanged = item && item.columns.map(x => x.key).includes(key);
		const style = didChanged
			? {
					backgroundColor: theme.palette.themeLighter,
					width: '100%',
					height: '100%',
			  }
			: undefined;
		return <div style={style}>{renderCellF({row})}</div>;
	};
}

export const renderEditCellSTD = (
	ii: number,
	col: GridViewColumn,
	handleLocalChange: (col: GridViewColumn, row: IRow) => () => Promise<void>,
) => {
	const colNumber = ii + 1;
	const renderEditCell = ({row, onClose}: RenderEditCellProps<IRow>) => {
		return (
			<>
				{col.renderCell!({row})}
				{col.noCalloutForm !== true && (
					<Callout
						alignTargetEdge={true}
						role='dialog'
						gapSpace={0}
						target={`[role="row"][aria-rowindex="${
							row.rowId + 2
						}"] > [role="gridcell"][aria-colindex="${colNumber}"]`}
						setInitialFocus
						calloutMinWidth={500}
						calloutWidth={500}
						calloutMaxWidth={500}
					>
						{col.calloutForm}
						<ActionButton onClick={handleLocalChange(col, row)}>
							{'Änderungen vornehmen'}
						</ActionButton>
					</Callout>
				)}
			</>
		);
	};
	return renderEditCell;
};

export const renderCalloutEditFormSTD = (
	col: GridViewColumn,
	selectableItems: any[] | undefined,
	control: Control<any>,
) => {
	const {key: name, name: label} = col;

	const props = {
		control,
		name,
		label,
	};

	switch (col.columnType) {
		case GridViewColumnType.Array:
			return (
				<ControlledTagPicker
					selectableItems={selectableItems ?? []}
					getKey={item => item.id}
					getName={item => item.name}
					{...props}
				/>
			);

		case GridViewColumnType.Date:
			return <ControlledDatePicker {...props} />;

		case GridViewColumnType.Phase:
			return <PhasePicker {...props} />;

		case GridViewColumnType.HTML:
			return <ControlledRichtextEditor {...props} />;

		case GridViewColumnType.Text:
			return <ControlledTextField {...props} />;

		default:
			return <></>;
	}
};
